import React, { useState, useRef, useCallback, useEffect } from "react";
import useRepairIssueInfo from "../store/useRepairIssueInfo";
import useNoiseInfo from "../store/useNoiseInfo";
import TextareaBox from "../../../components/TextareaBox";
import Tag from "../../../components/Tag";
import { NOISE_SUB_CODE, shouldShowLastIssueTime } from "../config/constants";
import { RepairSubItem } from "../interfaces/home";
import DateTimeField from "../components/DateTimeField";
import { getIssueDescriptionErrors, isNoiseMarked } from "../utils/issueValidation";
import { NoiseFeed } from "./NoiseFeed";
import styles from "./index.module.scss";

interface RepairIssueDescriptionProps {
  /** 已选中的故障维修子项（按展示顺序），逐项渲染一条问题描述 */
  subItems: RepairSubItem[];
  /** 上一步（回到预约项目）；只读态不需要 */
  onPrev?: () => void;
  /** 校验通过后进入下一步（回预约主流程「选择门店时间」）；只读态不需要 */
  onNext?: () => void;
  /**
   * 只读态：仅展示已填写的问题描述 / 附件 / 最近一次问题时间 / 异响反馈，
   * 隐藏底部「上一步/下一步」按钮、必填标记与填写引导（供预约详情「查看」复用同一组件）。
   */
  readOnly?: boolean;
}

/**
 * 故障维修「问题描述」——内联全屏阶段（由 homepage/EditingPage 渲染，不走独立路由）。
 * 每个选中的维修子项一条：问题描述（必填）+ 附件上传（选填）+ 最近一次问题时间。
 * - 最近一次问题时间：除异响103/外观内饰104 外的故障维修二级项（含其他108）均露出且必填
 *   （见 shouldShowLastIssueTime）。
 * - 异响子项（code=103）由异响子模块 NoiseFeed（编辑态 Edit / 只读态 Detail）渲染异响专属字段（不含最近一次问题时间）。
 * - 「其他」子项（code=108）与普通子项一致：问题描述（必填）+ 附件（选填）+ 最近一次问题时间（必填）。
 * 描述文本 / 附件 / 最近一次问题时间存于 useRepairIssueInfo；异响专属字段存于 useNoiseInfo。
 */
export default function RepairIssueDescription(
  props: RepairIssueDescriptionProps
) {
  const { subItems, onPrev, onNext, readOnly = false } = props;
  const { getIssue, setIssueDesc, setIssueAttachments, setIssueLastIssueTime } =
    useRepairIssueInfo();
  const { noiseInfo } = useNoiseInfo();

  // 校验标红状态：存储未通过的字段 key 集合
  const [errorFields, setErrorFields] = useState<Set<string>>(new Set());
  const pageRef = useRef<HTMLDivElement>(null);

  /** 单个字段错误自动清除（已填写后） */
  const clearError = useCallback((key: string) => {
    setErrorFields((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // 异响字段填写后自动清除标红（noiseInfo 变化时重新评估）
  useEffect(() => {
    setErrorFields((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      if (next.has("noise-position") && isNoiseMarked(noiseInfo)) {
        next.delete("noise-position");
        changed = true;
      }
      if (next.has("noise-recurrent") && noiseInfo.noiseRecurrent != null) {
        next.delete("noise-recurrent");
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [noiseInfo]);

  // 全部故障维修子项均展示问题描述反馈（含「其他」108）；
  // 异响反馈项（code=103）若存在则置顶展示（编辑态/详情只读态一致），其余项保持原相对顺序（稳定排序）
  const feedbackItems = subItems
    .slice()
    .sort(
      (a, b) =>
        (Number(a.code) === NOISE_SUB_CODE ? 0 : 1) -
        (Number(b.code) === NOISE_SUB_CODE ? 0 : 1)
    );

  // 异响是否被选中（校验规则见 utils/issueValidation）
  const isNoiseSelected = feedbackItems.some(
    (s) => Number(s.code) === NOISE_SUB_CODE
  );

  /** 滚动到第一个标红字段 */
  const scrollToFirstError = useCallback((errors: Set<string>) => {
    if (errors.size === 0 || !pageRef.current) return;
    // 按 DOM 顺序查找第一个带 data-field 且在错误集合中的元素
    const allFieldEls = pageRef.current.querySelectorAll<HTMLElement>('[data-field]');
    for (let i = 0; i < allFieldEls.length; i++) {
      const el = allFieldEls[i];
      if (errors.has(el.dataset.field!)) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
  }, []);

  const handleNext = () => {
    const errors = getIssueDescriptionErrors({
      feedbackItems,
      getIssue,
      isNoiseSelected,
      noiseInfo,
    });
    // 全部填齐：清除标红并进入下一步
    if (errors.size === 0) {
      setErrorFields((prev) => (prev.size === 0 ? prev : new Set()));
      onNext?.();
      return;
    }
    // 未填齐：标红并滚动到第一个错误位置（延一帧等 DOM 提交）
    setErrorFields(errors);
    requestAnimationFrame(() => scrollToFirstError(errors));
  };

  return (
    <div ref={pageRef} className={`${styles.page}${readOnly ? " " + styles.pageRo : ""}`}>
      <div className={styles.card}>
        {/* 顶部说明：问题描述（必填） + 橙色提示（只读态不展示引导/必填） */}
        <div className={styles.topTitleRow}>
          <span className={styles.topTitle}>问题描述</span>
          {!readOnly && <Tag type="success">必填</Tag>}
        </div>
        {!readOnly && (
          <div className={styles.topTip}>
            请详细描述需维修的项目问题，可上传照片/视频，我们将提前诊断及备件，以减少您到店的等待时长。
          </div>
        )}

        {feedbackItems.map((item, index) => {
          const code = Number(item.code);
          const isNoise = code === NOISE_SUB_CODE;
          // 最近一次问题时间：除异响103/外观内饰104/其他108 外均露出且必填
          const showLastIssueTime = shouldShowLastIssueTime(code);
          const issue = getIssue(item.code);
          return (
            <div key={item.code} className={styles.issueBlock}>
              <div className={styles.issueTitle}>
                {index + 1}. {item.name}问题描述
              </div>

              <div data-field={`desc-${item.code}`}>
                <TextareaBox
                  placeholder={`请描述您遇到的${item.name}问题`}
                  value={issue.desc}
                  onChange={(v) => {
                    setIssueDesc(item.code, v);
                    // 填写后自动清除该项标红
                    if (v.trim()) clearError(`desc-${item.code}`);
                  }}
                  maxLength={500}
                  readOnly={readOnly}
                  required={!readOnly}
                  error={errorFields.has(`desc-${item.code}`)}
                  enableVoiceInput={true}
                  enableAttachment={true}
                  attachmentValue={issue.attachmentIds}
                  onAttachmentChange={(ids) =>
                    setIssueAttachments(item.code, ids)
                  }
                />
              </div>

              {/* 最近一次问题时间：按 code 规则露出即必填；只读态无值时不展示 */}
              {showLastIssueTime && (!readOnly || issue.lastIssueTime) && (
                <div className={styles.lastIssueTimeRow} data-field={`time-${item.code}`}>
                  <DateTimeField
                    label="最近一次问题时间"
                    value={issue.lastIssueTime}
                    allowUnknown
                    onChange={(v) => {
                      setIssueLastIssueTime(item.code, v);
                      // 选择后自动清除该项标红
                      if (v) clearError(`time-${item.code}`);
                    }}
                    readOnly={readOnly}
                    required={!readOnly}
                    error={errorFields.has(`time-${item.code}`)}
                  />
                </div>
              )}

              {/* 异响子项：同一组件，readOnly 切换编辑/禁用态 */}
              {isNoise && <NoiseFeed readOnly={readOnly} errorFields={errorFields} />}
            </div>
          );
        })}
      </div>

      {!readOnly && (
        <div className={styles.bottomBar}>
          <div className={styles.preBtn} onClick={() => onPrev?.()}>
            上一步
          </div>
          <div
            className={styles.nextBtn}
            onClick={handleNext}
          >
            下一步
          </div>
        </div>
      )}
    </div>
  );
}
